import { useEffect, useRef, useState } from 'react';
import { usePublicClient } from 'wagmi';
import { BRIDGE_RECEIVER_ABI, decodeChainSwapFulfilled } from '../lib/bridgeCalldata';

// useBridgeDelivery
//
// TRUSTLESS delivery tracking for the LIVE mainnet bridge — no server status
// endpoint involved. Polls BridgeReceiverUpgradeable.fulfilled(swapId) on the
// DESTINATION chain directly (a public view function anyone can read, per
// the contract's own docs: "Public so any third party can verify status
// without an indexer"). The bridge_relay daemon is what actually calls
// fulfillSwap — this hook only observes the result.
//
// Once fulfilled flips true we do ONE extra eth_getLogs call (bounded to a
// recent block window) to recover the destination tx hash for the explorer
// link — best-effort only; a miss there doesn't change the trustless
// fulfilled=true verdict.
//
// If nothing has been delivered after `timeoutMs`, we report `stuck: true`
// (the customer can still trust `fulfilled` will eventually flip — the
// underlying burn is final — this is just a "something's slow" UX signal,
// polling continues either way).
//
// Returns { fulfilled, mintTxHash, mintBlockNumber, stuck, elapsedMs }.

const POLL_MS = 5000;
const DEFAULT_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes
// Kept small so the BEST-EFFORT ChainSwapFulfilled log scan fits the public
// RPC's getLogs limits (the frontend falls back to mainnet.base.org when no
// Alchemy key is embedded; a 20k-block range there returned HTTP 413). The mint
// always lands within a couple of minutes of the burn, so ~33 min of Base
// blocks is ample; delivery DETECTION uses fulfilled(swapId), not this scan.
const LOG_SCAN_BLOCK_WINDOW = 1000n;

export default function useBridgeDelivery(dstChainId, dstBridgeReceiverAddress, swapId, {
  timeoutMs = DEFAULT_TIMEOUT_MS,
} = {}) {
  const publicClient = usePublicClient({ chainId: Number(dstChainId) || undefined });
  const [fulfilled, setFulfilled] = useState(false);
  const [mintTxHash, setMintTxHash] = useState(null);
  const [mintBlockNumber, setMintBlockNumber] = useState(null);
  const [stuck, setStuck] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const startRef = useRef(null);

  useEffect(() => {
    if (!swapId || !dstBridgeReceiverAddress || !publicClient) return;
    if (startRef.current == null) startRef.current = Date.now();

    let cancelled = false;
    let timer = null;

    const tick = async () => {
      if (cancelled) return;
      const elapsed = Date.now() - startRef.current;
      setElapsedMs(elapsed);
      if (elapsed > timeoutMs) {
        setStuck(true);
        // Keep polling regardless — "stuck" is informational, not final.
      }

      try {
        const isFulfilled = await publicClient.readContract({
          address: dstBridgeReceiverAddress,
          abi: BRIDGE_RECEIVER_ABI,
          functionName: 'fulfilled',
          args: [swapId],
        });
        if (!cancelled && isFulfilled) {
          setFulfilled(true);
          setStuck(false);
          // Best-effort: fetch the ChainSwapFulfilled log for the tx hash.
          try {
            const head = await publicClient.getBlockNumber();
            const fromBlock = head > LOG_SCAN_BLOCK_WINDOW ? head - LOG_SCAN_BLOCK_WINDOW : 0n;
            const logs = await publicClient.getLogs({
              address: dstBridgeReceiverAddress,
              event: BRIDGE_RECEIVER_ABI.find((f) => f.name === 'ChainSwapFulfilled'),
              args: { swapId },
              fromBlock,
              toBlock: 'latest',
            });
            if (!cancelled && logs.length > 0) {
              const log = logs[logs.length - 1];
              setMintTxHash(log.transactionHash);
              setMintBlockNumber(log.blockNumber);
              // Cross-check the decoded event still matches (defence in depth).
              decodeChainSwapFulfilled(log);
            }
          } catch (_) {
            // Non-fatal — fulfilled=true already stands on the direct read.
          }
          return; // terminal — stop polling
        }
      } catch (_) {
        // Transient RPC hiccup — keep polling.
      }

      if (!cancelled) {
        timer = setTimeout(tick, POLL_MS);
      }
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [swapId, dstBridgeReceiverAddress, publicClient, timeoutMs]);

  return { fulfilled, mintTxHash, mintBlockNumber, stuck, elapsedMs };
}
